import { Coupon } from "app/statements/coupon";

export interface StatementPaymentAmountResponse {
  premium_amount_total: string;
  premium_amount_vat: string;
  standard_amount_total: string;
  standard_amount_vat: string;
  premium_min_amount_total: string;
  premium_min_amount_vat: string;
  should_be_individual_service: boolean;
  is_percentage_payment: boolean;
  tax_adisor_fee: string;
  taxando_fee: string;
  taxando_fee_vat: string;
}

export interface StatementPaymentAmountInterface {
  premiumAmountTotal: number;
  premiumAmountVat: number;
  standardAmountTotal: number;
  standardAmountVat: number;
  premiumNet: string;
  standardNet: string;
  vatPercentFormatted: string;
  currencySymbol: string;
  shouldBeIndividualService: boolean;
  premiumAmountTotalFormatted(coupon?: Coupon): string;
  standardAmountTotalFormatted(coupon?: Coupon): string;
}

export class StatementPaymentAmount implements StatementPaymentAmountInterface {
  premiumAmountTotal = 0;
  premiumAmountVat = 0;
  standardAmountTotal = 0;
  standardAmountVat = 0;
  shouldBeIndividualService = false;

  premiumAmountTotalBase: number;
  premiumAmountVatBase: number;
  standardAmountTotalBase: number;
  standardAmountVatBase: number;

  isPercentagePayment = false;
  premiumMinAmountTotal = 0;
  premiumMinAmountVat = 0;

  taxAdvisorFee = 0;
  taxandoFee = 0;
  taxandoFeeVat = 0;

  static fromResponse(response: StatementPaymentAmountResponse): StatementPaymentAmount {
    const statementPayment = new StatementPaymentAmount();
    statementPayment.premiumAmountTotal = Number(response.premium_amount_total);
    statementPayment.premiumAmountVat = Number(response.premium_amount_vat);
    statementPayment.standardAmountTotal = Number(response.standard_amount_total);
    statementPayment.standardAmountVat = Number(response.standard_amount_vat);
    statementPayment.shouldBeIndividualService = response.should_be_individual_service;

    statementPayment.premiumAmountTotalBase = Number(response.premium_amount_total);
    statementPayment.premiumAmountVatBase = Number(response.premium_amount_vat);
    statementPayment.standardAmountTotalBase = Number(response.standard_amount_total);
    statementPayment.standardAmountVatBase = Number(response.standard_amount_vat);
    statementPayment.premiumMinAmountTotal = Number(response.premium_min_amount_total);
    statementPayment.premiumMinAmountVat = Number(response.premium_min_amount_vat);
    statementPayment.isPercentagePayment = response.is_percentage_payment;
    statementPayment.taxAdvisorFee = Number(response.tax_adisor_fee);
    statementPayment.taxandoFee = Number(response.taxando_fee);
    statementPayment.taxandoFeeVat = Number(response.taxando_fee_vat);

    return statementPayment;
  }

  public get isFree(): boolean {
    return this.standardAmountTotal === 0;
  }

  public get vatPercentFormatted(): string {
    const net = Number(this.premiumAmountTotal) - Number(this.premiumAmountVat);
    if (net <= 0) return "0%";
    return String(Math.round((Number(this.premiumAmountVat) / net) * 100)) + "%";
  }

  public get premiumNet(): string {
    return (Number(this.premiumAmountTotal) - Number(this.premiumAmountVat)).toFixed(2);
  }

  public get standardNet(): string {
    return (Number(this.standardAmountTotal) - Number(this.standardAmountVat)).toFixed(2);
  }

  public get currencySymbol(): string {
    return "€";
  }

  public standardAmountTotalFormatted(coupon?: Coupon): string {
    if (coupon && coupon.couponAmount) {
      return (Number(this.standardAmountTotal) - Number(coupon.amount(this.standardAmountTotal))).toFixed(2);
    }

    return this.standardAmountTotal.toFixed(2);
  }

  public premiumAmountTotalFormatted(coupon?: Coupon): string {
    if (coupon && coupon.couponAmount) {
      return (Number(this.premiumAmountTotal) - Number(coupon.amount(this.premiumAmountTotal))).toFixed(2);
    }

    return this.premiumAmountTotal.toFixed(2);
  }

  public resetToBase(): void {
    this.premiumAmountTotal = this.premiumAmountTotalBase;
    this.premiumAmountVat = this.premiumAmountVatBase;
    this.standardAmountTotal = this.standardAmountTotalBase;
    this.standardAmountVat = this.standardAmountVatBase;
  }

  public applyStandardCouponTotal(couponTotal: number, vatRate?: number): void {
    this.standardAmountTotal = Number(couponTotal);
    this.standardAmountVat = this.calculateVat(this.standardAmountTotal, vatRate, this.standardAmountVatBase, this.standardAmountTotalBase);
  }

  public applyPremiumCouponTotal(couponTotal: number, vatRate?: number): void {
    this.premiumAmountTotal = Number(couponTotal);
    this.premiumAmountVat = this.calculateVat(this.premiumAmountTotal, vatRate, this.premiumAmountVatBase, this.premiumAmountTotalBase);
  }

  private calculateVat(total: number, vatRate: number | undefined, baseVat: number, baseTotal: number): number {
    if (typeof vatRate === "number") {
      return total * vatRate;
    }

    if (baseTotal > 0) {
      return total * (baseVat / baseTotal);
    }

    return 0;
  }
}
